import { ChatBubble, Link as LinkIcon, MailOut, SendDiagonal } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import type { InvitationChannelMode } from '@/stores/invitation-store';
import {
  ChannelPicker,
  ConnectMailboxAffordance,
  type ChannelPickerRow,
} from './outreach/channel-picker';
import type { InvitationChannelCapabilities } from './invitation-channel-helpers';

const K = 'invitations.modal.channel.rows';

interface InvitationChannelPickerProps {
  value: InvitationChannelMode;
  onChange: (mode: InvitationChannelMode) => void;
  capabilities: InvitationChannelCapabilities;
}

/**
 * Delivery-channel radio rows of the affiliation channel step: the shareable
 * link (always), email through the connected mailbox (shown when the brand
 * knows an email; disabled with an inline connect affordance when no mailbox is
 * connected), and the independent Instagram DM and comment-reply rows (each
 * shown only when the server reports the matching capability). A thin config
 * wrapper over the shared {@link ChannelPicker}.
 */
export const InvitationChannelPicker = ({
  value,
  onChange,
  capabilities,
}: InvitationChannelPickerProps) => {
  const { t } = useTranslation();
  const emailNeutral = capabilities.emailEnabled || capabilities.emailStatusPending;

  const rows: ChannelPickerRow<InvitationChannelMode>[] = [
    {
      value: 'link',
      icon: LinkIcon,
      title: t(`${K}.link.title`),
      description: t(`${K}.link.description`),
    },
  ];
  if (capabilities.emailAvailable) {
    rows.push({
      value: 'email',
      icon: MailOut,
      disabled: !capabilities.emailEnabled,
      title: t(`${K}.email.title`),
      description: emailNeutral ? t(`${K}.email.description`) : t(`${K}.email.notConnected`),
      trailing: emailNeutral ? undefined : (
        <ConnectMailboxAffordance label={t(`${K}.email.connectLink`)} />
      ),
    });
  }
  if (capabilities.dmAvailable) {
    rows.push({
      value: 'dm',
      icon: SendDiagonal,
      title: t(`${K}.dm.title`),
      description: t(`${K}.dm.description`),
    });
  }
  if (capabilities.commentAvailable) {
    rows.push({
      value: 'comment',
      icon: ChatBubble,
      title: t(`${K}.comment.title`),
      description: t(`${K}.comment.description`),
    });
  }

  return <ChannelPicker rows={rows} value={value} onChange={onChange} />;
};
